/**
 * Helpers para el modelo de archivos por carpeta que evita el límite de 1 MiB
 * por documento de Firestore.
 *
 * Contexto: DAYSI denormaliza los archivos de una carpeta en el array `files[]`
 * del doc "self" de esa carpeta. Cada entrada pesa ~478 B (domina la URL de
 * Storage), así que al pasar ~2.000 archivos el doc supera 1.048.576 bytes y
 * Firestore rechaza toda escritura con `invalid-argument`.
 *
 * Solución (Opción 3, capacidad ilimitada, estructura idéntica): los archivos
 * viven en `files[]` hasta FILES_ARRAY_THRESHOLD; pasado ese punto se guardan
 * como documentos autosuficientes en `folderDoc/__files__/{fileId}` y el doc
 * self se marca con `filesOverflow: true`. Las vistas leen ambas fuentes y las
 * fusionan (dedup por `fileId`). Compatible hacia atrás, sin migración.
 */

// Nombre de la subcolección de archivos "overflow".
//  - Contiene '-', que un btoa() de dígitos (los IDs de subcarpetas) nunca
//    produce, así que no colisiona con ellos.
//  - NO puede empezar y terminar con '__' (Firestore reserva el patrón
//    /__.*__/ y rechaza el commit con invalid-argument — verificado en pruebas).
export const FILES_SUBCOLLECTION = 'files-store';

// Umbral de archivos en el array antes de derivar a la subcolección. Margen
// seguro bajo los ~2.100 que reventaron 1 MiB en la reproducción (2026-07-23).
export const FILES_ARRAY_THRESHOLD = 1500;

/**
 * ¿Los archivos nuevos de esta carpeta deben ir a la subcolección __files__?
 * True si ya se marcó overflow, o si el array alcanzó el umbral.
 * @param {object|undefined} folderData  data() del doc self de la carpeta.
 * @returns {boolean}
 */
export const shouldOverflowToSubcollection = (folderData) => {
  if (!folderData) return false;
  if (folderData.filesOverflow === true) return true;
  const arr = folderData.files;
  return Array.isArray(arr) && arr.length >= FILES_ARRAY_THRESHOLD;
};

/**
 * Entrada canónica de un archivo. MISMA forma en el array y en __files__ para
 * que el render por-entrada de las vistas no tenga que distinguir el origen.
 */
export const buildFileEntry = ({ name, url, type, date_of_upload, fileId }) =>
  ({ name, url, type, date_of_upload, fileId });

/**
 * Fusiona el array `files[]` con los docs de __files__, deduplicando por
 * `fileId` (gana la entrada del array si hay choque). Estable. Tolera
 * indefinidos y entradas sin `fileId` (las conserva todas).
 * @param {object[]|undefined} arrayFiles
 * @param {object[]|undefined} subDocs  data() de cada doc de __files__.
 * @returns {object[]}
 */
export const mergeFolderFiles = (arrayFiles, subDocs) => {
  const out = [];
  const seen = new Set();
  const push = (e) => {
    if (!e) return;
    const id = e.fileId;
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    }
    out.push(e);
  };
  (Array.isArray(arrayFiles) ? arrayFiles : []).forEach(push);
  (Array.isArray(subDocs) ? subDocs : []).forEach(push);
  return out;
};
